/**
 * Cross-tab single-flight for the JWT refresh call (ticket 86eypvcbv).
 *
 * The backend rotates the refresh token and blacklists the one it replaces.
 * Each tab holds its own in-memory copy of the pair, so the moment one tab
 * rotates, every other tab is holding a token the server has retired. Left
 * alone, the next request from those tabs presents it, takes a 401 and logs
 * the whole browser out — the "Remember Me never keeps you signed in" report.
 *
 * So: at most one rotation happens per browser, and every other tab adopts its
 * result, either from the shared localStorage record or from the peer's own
 * broadcast.
 */
import { AUTH_STORAGE_KEY, useAuthStore } from '@/stores/auth'
import { onAuthMessage, postAuthMessage } from '@/stores/authChannel'

/** How long a peer's `refresh-started` counts as "a rotation is in flight". */
const PEER_REFRESH_WINDOW_MS = 10_000
/** How long to wait for that peer to publish the pair it received. */
const PEER_TOKEN_WAIT_MS = 5_000

export class SessionRefreshError extends Error {
  /**
   * True only when the session itself is finished: the server rejected the
   * refresh token, or there is none. Every other failure (network down,
   * malformed response) leaves the stored pair usable, so callers must NOT
   * end the session on it.
   */
  readonly sessionExpired: boolean
  readonly reason: unknown

  constructor(message: string, sessionExpired: boolean, reason: unknown = null) {
    super(message)
    this.name = 'SessionRefreshError'
    this.sessionExpired = sessionExpired
    this.reason = reason
  }
}

interface PersistedTokens {
  access: string
  refresh: string
}

// Coordination state, not a cache: single-flight has to be per tab, and the
// tab is the module.
let inFlight: Promise<string> | null = null
let peerRefreshStartedAt = 0
const waiters = new Set<(access: string) => void>()

onAuthMessage((message) => {
  if (typeof message === 'string') {
    return
  }
  if (message.type === 'refresh-started') {
    peerRefreshStartedAt = Date.now()
    return
  }
  useAuthStore.getState().setTokens(message.access, message.refresh)
  peerRefreshStartedAt = 0
  for (const waiter of waiters) {
    waiter(message.access)
  }
  waiters.clear()
})

/** The pair another tab last wrote. localStorage is the shared record. */
function readPersistedTokens(): PersistedTokens | null {
  const raw = localStorage.getItem(AUTH_STORAGE_KEY)
  if (!raw) {
    return null
  }
  const envelope = JSON.parse(raw) as { state?: { token?: unknown; refreshToken?: unknown } }
  const access = envelope.state?.token
  const refresh = envelope.state?.refreshToken
  if (typeof access !== 'string' || typeof refresh !== 'string') {
    return null
  }
  return { access, refresh }
}

/**
 * The access token to use when `presented` is no longer this browser's current
 * refresh token, because another tab rotated it. Two places can already hold
 * the replacement: the shared localStorage record, and this tab's own store
 * when the peer's broadcast landed first.
 */
function replacementFor(presented: string | null): string | null {
  const persisted = readPersistedTokens()
  if (persisted && persisted.refresh !== presented) {
    useAuthStore.getState().setTokens(persisted.access, persisted.refresh)
    return persisted.access
  }
  const { token, refreshToken } = useAuthStore.getState()
  return token && refreshToken !== presented ? token : null
}

function peerIsRefreshing(): boolean {
  return Date.now() - peerRefreshStartedAt < PEER_REFRESH_WINDOW_MS
}

function waitForPeerTokens(timeoutMs: number): Promise<string | null> {
  return new Promise((resolve) => {
    const waiter = (access: string): void => {
      clearTimeout(timer)
      waiters.delete(waiter)
      resolve(access)
    }
    const timer = setTimeout(() => {
      waiters.delete(waiter)
      resolve(null)
    }, timeoutMs)
    waiters.add(waiter)
  })
}

/**
 * BroadcastChannel delivers on the task queue, so a peer's rotation posted
 * while our own request was in flight has not been handled yet inside the task
 * that received our 401. Yield once so those messages land before we judge the
 * session dead.
 */
function flushPeerMessages(): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, 0)
  })
}

/** Our token was rejected. Find out whether a peer replaced it a moment ago. */
async function recoverFromPeerRotation(presented: string): Promise<string | null> {
  await flushPeerMessages()
  const replacement = replacementFor(presented)
  if (replacement) {
    return replacement
  }
  if (!peerIsRefreshing()) {
    return null
  }
  const fromPeer = await waitForPeerTokens(PEER_TOKEN_WAIT_MS)
  return fromPeer ?? replacementFor(presented)
}

function storeRotatedPair(payload: unknown): string {
  const body = payload as { access?: unknown; refresh?: unknown }
  if (typeof body.access !== 'string') {
    throw new SessionRefreshError('The refresh response carried no access token', false)
  }
  const refresh = typeof body.refresh === 'string' ? body.refresh : null
  useAuthStore.getState().setTokens(body.access, refresh)
  postAuthMessage({ type: 'refresh-succeeded', access: body.access, refresh })
  return body.access
}

async function requestRotation(): Promise<string> {
  const presented = useAuthStore.getState().refreshToken
  if (!presented) {
    throw new SessionRefreshError('This session has no refresh token', true)
  }
  postAuthMessage({ type: 'refresh-started' })

  let response: Response
  try {
    response = await fetch('/api/v1/auth/refresh/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh: presented }),
    })
  } catch (transportError) {
    // The transport failed, not the token. Ending the session here is what
    // turns a laptop waking before its wifi into "Remember Me never works".
    throw new SessionRefreshError('Could not reach the refresh endpoint', false, transportError)
  }

  if (response.status === 401) {
    const recovered = await recoverFromPeerRotation(presented)
    if (recovered) {
      return recovered
    }
    throw new SessionRefreshError('The server rejected the refresh token', true)
  }
  if (!response.ok) {
    throw new SessionRefreshError(`The refresh call failed (HTTP ${response.status})`, false)
  }
  return storeRotatedPair(await response.json())
}

async function runRefresh(): Promise<string> {
  const held = useAuthStore.getState().refreshToken
  const replacement = replacementFor(held)
  if (replacement) {
    return replacement
  }
  if (peerIsRefreshing()) {
    const fromPeer = await waitForPeerTokens(PEER_TOKEN_WAIT_MS)
    if (fromPeer) {
      return fromPeer
    }
    const afterWait = replacementFor(held)
    if (afterWait) {
      return afterWait
    }
  }
  return requestRotation()
}

/**
 * Resolve with an access token this tab may use. At most one rotation runs at
 * a time across the whole browser, and every caller gets its result. Throws
 * SessionRefreshError; only `sessionExpired` on it means log the user out.
 */
export function refreshSession(): Promise<string> {
  if (!inFlight) {
    inFlight = runRefresh().finally(() => {
      inFlight = null
    })
  }
  return inFlight
}
